import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { STRATEGY_QUERY_KEYS, WIZARD_QUERY_KEYS } from '@/constants/query-keys';
import { wizardApi } from '@/repository/wizard';
import type {
  BackgroundJobResponse,
  BriefSuggestionsRead,
  BriefValidationRead,
  CampaignAttentionResponse,
  CampaignLaunchMode,
  CampaignURLValidationResponse,
  SearchTermNarrativeResponse,
  SingleCampaignBuildRead,
  SingleCampaignCreate,
  SingleCampaignEligibilityRead,
  StrategyCreate,
  StrategyObjective,
  StrategyWizardSessionData,
  UpdateCampaignsRequest,
  UpdateMediaPlanRequest,
} from '@/types/generated';

// New or edited campaigns stale the grid's URL, attention and search-term snapshots.
export const invalidateCampaignChecks = (
  qc: QueryClient,
  strategyId: number,
): void => {
  for (const queryKey of [
    WIZARD_QUERY_KEYS.validateUrls(strategyId),
    WIZARD_QUERY_KEYS.attention(strategyId),
    WIZARD_QUERY_KEYS.searchTermNarrative(strategyId),
  ]) {
    void qc.invalidateQueries({ queryKey });
  }
};

interface RegenerateMediaPlanVars {
  strategyId: number;
  brief?: StrategyCreate;
}

interface RebuildSingleCampaignVars {
  strategyId: number;
  body: SingleCampaignCreate;
}

interface PushCampaignsVars {
  strategyId: number;
  launchMode: CampaignLaunchMode;
}

export const wizardDraftQueryOptions = (strategyId: number) =>
  queryOptions({
    queryKey: WIZARD_QUERY_KEYS.draft(strategyId),
    queryFn: () => wizardApi.getDraft(strategyId),
    retry: false,
    staleTime: 30_000,
  });

export const useWizardSession = (
  strategyId: number | null,
): UseQueryResult<StrategyWizardSessionData, Error> =>
  useQuery({
    ...wizardDraftQueryOptions(strategyId ?? 0),
    enabled: strategyId != null,
  });

// Uncached: a brief opened after a strategy was created must dedupe its name against it.
export const useBriefSuggestions = (
  enabled: boolean,
): UseQueryResult<BriefSuggestionsRead, Error> =>
  useQuery({
    queryKey: WIZARD_QUERY_KEYS.briefSuggestions,
    queryFn: () => wizardApi.getBriefSuggestions(),
    enabled,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

// Lead-gen eligibility reads live conversion volume, so an objective toggled back reuses it.
export const usePinEligibility = (
  objective: StrategyObjective | undefined,
): UseQueryResult<SingleCampaignEligibilityRead, Error> =>
  useQuery({
    queryKey: WIZARD_QUERY_KEYS.pinEligibility(objective ?? ''),
    queryFn: () =>
      wizardApi.getSingleCampaignEligibility(objective as StrategyObjective),
    enabled: objective !== undefined,
    staleTime: 60_000,
  });

export const useCreateSingleCampaign = (): UseMutationResult<
  SingleCampaignBuildRead,
  Error,
  SingleCampaignCreate
> =>
  useMutation<SingleCampaignBuildRead, Error, SingleCampaignCreate>({
    mutationFn: (body) => wizardApi.createSingleCampaign(body),
  });

export const useRebuildSingleCampaign = (): UseMutationResult<
  BackgroundJobResponse,
  Error,
  RebuildSingleCampaignVars
> =>
  useMutation<BackgroundJobResponse, Error, RebuildSingleCampaignVars>({
    mutationFn: ({ strategyId, body }) =>
      wizardApi.rebuildSingleCampaign(strategyId, body),
  });

export const useValidateBrief = (): UseMutationResult<
  BriefValidationRead,
  Error,
  StrategyCreate
> =>
  useMutation<BriefValidationRead, Error, StrategyCreate>({
    mutationFn: (brief) => wizardApi.validateBrief(brief),
  });

export const useGenerateCampaigns = (): UseMutationResult<
  BackgroundJobResponse,
  Error,
  number
> =>
  useMutation<BackgroundJobResponse, Error, number>({
    mutationFn: (strategyId) => wizardApi.generateCampaigns(strategyId),
  });

export interface UpdateCampaignsVariables {
  strategyId: number;
  body: UpdateCampaignsRequest;
}

export const useUpdateCampaigns = (): UseMutationResult<
  StrategyWizardSessionData,
  Error,
  UpdateCampaignsVariables
> => {
  const qc = useQueryClient();
  return useMutation<
    StrategyWizardSessionData,
    Error,
    UpdateCampaignsVariables
  >({
    mutationFn: ({ strategyId, body }) =>
      wizardApi.updateCampaigns(strategyId, body),
    onSuccess: (data, { strategyId }) => {
      qc.setQueryData<StrategyWizardSessionData>(
        WIZARD_QUERY_KEYS.draft(strategyId),
        data,
      );
      invalidateCampaignChecks(qc, strategyId);
    },
  });
};

export interface UpdateMediaPlanVariables {
  strategyId: number;
  body: UpdateMediaPlanRequest;
}

export const useUpdateMediaPlan = (): UseMutationResult<
  StrategyWizardSessionData,
  Error,
  UpdateMediaPlanVariables
> => {
  const qc = useQueryClient();
  return useMutation<
    StrategyWizardSessionData,
    Error,
    UpdateMediaPlanVariables
  >({
    mutationFn: ({ strategyId, body }) =>
      wizardApi.updateMediaPlan(strategyId, body),
    onSuccess: (data, { strategyId }) => {
      qc.setQueryData<StrategyWizardSessionData>(
        WIZARD_QUERY_KEYS.draft(strategyId),
        data,
      );
      // A plan goal edit relinks the campaigns already built from it.
      void qc.invalidateQueries({
        queryKey: STRATEGY_QUERY_KEYS.campaigns(strategyId),
      });
      invalidateCampaignChecks(qc, strategyId);
    },
  });
};

export const useRegenerateCampaigns = (): UseMutationResult<
  BackgroundJobResponse,
  Error,
  number
> =>
  useMutation<BackgroundJobResponse, Error, number>({
    mutationFn: (strategyId) => wizardApi.regenerateCampaigns(strategyId),
  });

export const useRegenerateMediaPlan = (): UseMutationResult<
  BackgroundJobResponse,
  Error,
  RegenerateMediaPlanVars
> =>
  useMutation<BackgroundJobResponse, Error, RegenerateMediaPlanVars>({
    mutationFn: ({ strategyId, brief }) =>
      wizardApi.regenerateMediaPlan(strategyId, brief),
  });

export const usePushCampaigns = (): UseMutationResult<
  BackgroundJobResponse,
  Error,
  PushCampaignsVars
> =>
  useMutation<BackgroundJobResponse, Error, PushCampaignsVars>({
    mutationFn: ({ strategyId, launchMode }) =>
      wizardApi.pushCampaigns(strategyId, launchMode),
  });

// A query (not a mutation) so both the review screen and the per-campaign
// editor read the same cached invalid-URL set from a single validate call.
export const useCampaignUrlValidation = (
  strategyId: number,
  enabled: boolean,
): UseQueryResult<CampaignURLValidationResponse, Error> =>
  useQuery({
    queryKey: WIZARD_QUERY_KEYS.validateUrls(strategyId),
    queryFn: () => wizardApi.validateUrls(strategyId),
    enabled,
    staleTime: 5 * 60_000,
  });

// Validates a caller-supplied URL list (the editor's live, possibly-unsaved
// draft URLs) so reachability errors surface without a save-and-return cycle.
// Keyed on the URL set so distinct edits cache independently.
export const useDraftUrlValidation = (
  strategyId: number,
  urls: string[],
): UseQueryResult<CampaignURLValidationResponse, Error> =>
  useQuery({
    queryKey: [...WIZARD_QUERY_KEYS.validateUrls(strategyId), urls],
    queryFn: () => wizardApi.validateUrls(strategyId, urls),
    enabled: urls.length > 0,
    staleTime: 5 * 60_000,
  });

// A query (not a mutation) so both the review screen and the confirm dialog
// read the same cached attention snapshot from a single fetch.
export const useCampaignAttention = (
  strategyId: number,
  enabled: boolean,
): UseQueryResult<CampaignAttentionResponse, Error> =>
  useQuery({
    queryKey: WIZARD_QUERY_KEYS.attention(strategyId),
    queryFn: () => wizardApi.getAttention(strategyId),
    enabled,
    staleTime: 5 * 60_000,
  });

// A query (not a mutation) so the review screen reads a single cached
// search-term narrative snapshot rather than refetching per render.
export const useSearchTermNarrative = (
  strategyId: number,
  enabled: boolean,
): UseQueryResult<SearchTermNarrativeResponse, Error> =>
  useQuery({
    queryKey: WIZARD_QUERY_KEYS.searchTermNarrative(strategyId),
    queryFn: () => wizardApi.getSearchTermNarrative(strategyId),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useCancelDraft = (): UseMutationResult<void, Error, number> => {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (strategyId) => wizardApi.cancelDraft(strategyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STRATEGY_QUERY_KEYS.listBase });
    },
  });
};
